import React, { useState } from 'react';

import { Button, Group, Modal, Stack, Text, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertTriangle, IconArrowLeft, IconMailOpened } from '@tabler/icons-react';
import useResetPassword from 'hooks/auth/useResetPassword';
import { emailValidator } from 'utils/form-validators';

const ForgotPasswordModal = ({
  opened,
  onClose,
  onBackToLogin,
}: {
  opened: boolean;
  onClose: () => void;
  onBackToLogin: () => void;
}): JSX.Element => {
  const resetPasswordMutation = useResetPassword();
  const [submitted, setSubmitted] = useState(false);

  const form = useForm({
    initialValues: { email: '' },
    validate: { email: value => emailValidator(value) },
  });

  const handleSubmit = (formValues: { email: string }): void => {
    resetPasswordMutation.mutate(formValues, {
      onSuccess: () => setSubmitted(true),
      onError: () => setSubmitted(true),
    });
  };

  const handleClose = (): void => {
    onClose();
    form.reset();
    resetPasswordMutation.reset();
    setSubmitted(false);
  };

  return (
    <Modal
      centered
      opened={opened}
      onClose={handleClose}
      title={submitted ? null : <Title order={4}>Reset Password</Title>}
      size='lg'
    >
      {submitted ? (
        <Stack px='xl' pb='md' align='center' spacing='xl'>
          <IconMailOpened size={64} stroke={1} color='gray' />
          <Stack spacing='xs' align='center'>
            <Text weight={600} size='md'>
              Check your inbox
            </Text>
            <Text color='dimmed' size='sm' align='center'>
              If an account matches this email, you&apos;ll receive reset instructions shortly. Check your
              spam folder if you don&apos;t see them.
            </Text>
          </Stack>
          <Button
            fullWidth
            leftIcon={<IconArrowLeft size={16} />}
            onClick={() => {
              form.reset();
              resetPasswordMutation.reset();
              setSubmitted(false);
              onBackToLogin();
            }}
          >
            Back to Login
          </Button>
        </Stack>
      ) : (
        <Stack px='xl' pb='md'>
          <Text color='dimmed' size='sm'>
            Enter the email you used when you joined and we&apos;ll send you instructions to reset your
            password.
          </Text>
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack>
              <TextInput
                label='Email'
                placeholder='jane@mygym.com'
                required
                readOnly={resetPasswordMutation.isLoading}
                {...form.getInputProps('email')}
                error={
                  form.errors.email ? (
                    <Group spacing={4} noWrap>
                      <IconAlertTriangle size={14} />
                      <span>{form.errors.email}</span>
                    </Group>
                  ) : null
                }
              />
              <Button type='submit' loading={resetPasswordMutation.isLoading} mt='md'>
                Send Email
              </Button>
              <Button variant='subtle' color='kiloblue' fw={700} onClick={handleClose}>
                Cancel
              </Button>
            </Stack>
          </form>
        </Stack>
      )}
    </Modal>
  );
};

export default ForgotPasswordModal;
